/**
 * Placing an uploaded picture on the design (pure; twin of app/design/basemap/align.py, tested in align.test.ts).
 *
 * A placement maps the picture's pixels (px right, py down) to local metres (x east, y north):
 * x = a·px + b·py + c, y = d·px + e·py + f. Two control points give a similarity (move, turn, one scale), three an
 * affine; the drag handles move, turn and scale it about a point. The server solves control points again and checks
 * every placement.
 */
import type { Vec2 } from "../editor/geo.ts";

export type Affine = [number, number, number, number, number, number];
export interface ControlPoint {
  image: Vec2;
  enu: Vec2;
}

export const apply = (m: Affine, p: Vec2): Vec2 => [m[0] * p[0] + m[1] * p[1] + m[2], m[3] * p[0] + m[4] * p[1] + m[5]];

export function invert(m: Affine): Affine {
  const [a, b, c, d, e, f] = m;
  const det = a * e - b * d;
  if (Math.abs(det) < 1e-18) throw new Error("flat placement");
  const ia = e / det;
  const ib = -b / det;
  const id = -d / det;
  const ie = a / det;
  return [ia, ib, -(ia * c + ib * f), id, ie, -(id * c + ie * f)];
}

/** Metres per picture pixel (the square root of the area scale). */
export const metresPerPx = (m: Affine) => Math.sqrt(Math.abs(m[0] * m[4] - m[1] * m[3]));

/** The similarity through two control points; null when the points are too close. */
export function similarityFromPoints(p1: ControlPoint, p2: ControlPoint): Affine | null {
  // the picture's y runs down: (x, y) = k · (px − i·py) + t with complex k (turn and scale)
  const a1: Vec2 = [p1.image[0], -p1.image[1]];
  const a2: Vec2 = [p2.image[0], -p2.image[1]];
  const da: Vec2 = [a2[0] - a1[0], a2[1] - a1[1]];
  const dq: Vec2 = [p2.enu[0] - p1.enu[0], p2.enu[1] - p1.enu[1]];
  const den = da[0] * da[0] + da[1] * da[1];
  if (Math.sqrt(den) < 10 || Math.hypot(dq[0], dq[1]) < 0.1) return null;
  const kr = (dq[0] * da[0] + dq[1] * da[1]) / den;
  const ki = (dq[1] * da[0] - dq[0] * da[1]) / den;
  const tr = p1.enu[0] - (kr * a1[0] - ki * a1[1]);
  const ti = p1.enu[1] - (kr * a1[1] + ki * a1[0]);
  return [kr, ki, tr, ki, -kr, ti];
}

/** The affine through three control points; null when they are on one line. */
export function affineFromPoints(p: ControlPoint[]): Affine | null {
  if (p.length !== 3) return null;
  const [[x1, y1], [x2, y2], [x3, y3]] = p.map((q) => q.image) as [Vec2, Vec2, Vec2];
  const det = x1 * (y2 - y3) - x2 * (y1 - y3) + x3 * (y1 - y2);
  if (Math.abs(det) < 100) return null;
  const solve = (v: [number, number, number]): [number, number, number] => {
    const [v1, v2, v3] = v;
    const a = (v1 * (y2 - y3) - v2 * (y1 - y3) + v3 * (y1 - y2)) / det;
    const b = (x1 * (v2 - v3) - x2 * (v1 - v3) + x3 * (v1 - v2)) / det;
    const c = (x1 * (y2 * v3 - y3 * v2) - x2 * (y1 * v3 - y3 * v1) + x3 * (y1 * v2 - y2 * v1)) / det;
    return [a, b, c];
  };
  const [a, b, c] = solve(p.map((q) => q.enu[0]) as [number, number, number]);
  const [d, e, f] = solve(p.map((q) => q.enu[1]) as [number, number, number]);
  return [a, b, c, d, e, f];
}

/** The placement for 2 or 3 control points, and its largest miss in metres. */
export function fromControlPoints(points: ControlPoint[]): { m: Affine; miss: number } | null {
  const m = points.length === 2 ? similarityFromPoints(points[0]!, points[1]!) : points.length === 3 ? affineFromPoints(points) : null;
  if (!m) return null;
  const miss = Math.max(...points.map((q) => {
    const r = apply(m, q.image);
    return Math.hypot(r[0] - q.enu[0], r[1] - q.enu[1]);
  }));
  return { m, miss };
}

/** A first placement: the picture centred on `centre`, north up, `mpp` metres per pixel. */
export function initialPlacement(width: number, height: number, centre: Vec2, mpp: number): Affine {
  return [mpp, 0, centre[0] - (width / 2) * mpp, 0, -mpp, centre[1] + (height / 2) * mpp];
}

/** The placement moved by `delta` metres. */
export const moved = (m: Affine, delta: Vec2): Affine => [m[0], m[1], m[2] + delta[0], m[3], m[4], m[5] + delta[1]];

/** The placement turned by `deg` (counter-clockwise) and scaled by `k` about the map point `pivot`. */
export function turnedScaled(m: Affine, pivot: Vec2, deg: number, k: number): Affine {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r) * k;
  const sin = Math.sin(r) * k;
  // q' = R·(q − pivot) + pivot, q = M·p
  const [a, b, c, d, e, f] = m;
  return [cos * a - sin * d, cos * b - sin * e, cos * (c - pivot[0]) - sin * (f - pivot[1]) + pivot[0], sin * a + cos * d, sin * b + cos * e, sin * (c - pivot[0]) + cos * (f - pivot[1]) + pivot[1]];
}

/** Where the picture's corners land on the map (top-left, top-right, bottom-right, bottom-left). */
export const corners = (m: Affine, width: number, height: number): Vec2[] => [apply(m, [0, 0]), apply(m, [width, 0]), apply(m, [width, height]), apply(m, [0, height])];

/** Whether the server will take the placement (scale between 1 mm and 5 m a pixel, not stretched over 4×). */
export function plausible(m: Affine): boolean {
  if (!m.every(Number.isFinite)) return false;
  const sx = Math.hypot(m[0], m[3]);
  const sy = Math.hypot(m[1], m[4]);
  return Math.abs(m[0] * m[4] - m[1] * m[3]) > 1e-12 && Math.min(sx, sy) >= 0.001 && Math.max(sx, sy) <= 5 && Math.max(sx, sy) / Math.min(sx, sy) <= 4;
}
